import { History } from "lucide-react";
import { useState } from "react";
import { formatDate } from "../lib/formatDate";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "./ui/pagination";
import {
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Table,
} from "./ui/table";
import { Swap, useGetSwapsQuery } from "../generated/graphql";
import { useNavigate } from "react-router-dom";
import { formatNumberToString } from "../lib/formatNumbers";

interface SwapProps {
  swaps?: Swap[];
  page: number;
  onPrevious: () => void;
  onNext: () => void;
}
const SwapsTableBase = ({ swaps, page, onPrevious, onNext }: SwapProps) => {
  const navigate = useNavigate();
  return (
    <Card className="xl:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg font-semibold flex items-center">
          Swap History
          <History className="ml-2 h-4 w-4 opacity-50" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {swaps?.length === 0 ? (
          <div className="text-center m-6">No swaps found</div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>From Currency</TableHead>
                  <TableHead>From Amount</TableHead>
                  <TableHead>To Currency</TableHead>
                  <TableHead>To Amount</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {swaps?.map((swap, idx) => (
                  <TableRow
                    key={idx}
                    onClick={() => {
                      navigate(`/swaps/${swap.id}`);
                    }}
                  >
                    <TableCell>{swap.id?.slice(0, 6)}...</TableCell>
                    <TableCell
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginRight: "10px",
                        textWrap: "nowrap",
                      }}
                    >
                      <img
                        src={swap.fromMint.img ?? ""}
                        alt={""}
                        className="w-6 h-6 mr-2"
                      />
                      {swap.fromMint.symbol}
                    </TableCell>
                    <TableCell>
                      {formatNumberToString(swap.fromAmount) ?? "N/A"}
                    </TableCell>
                    <TableCell
                      style={{
                        display: "flex",
                        alignItems: "center",
                        textWrap: "nowrap",
                      }}
                    >
                      <img
                        src={swap.toMint.img ?? ""}
                        alt={""}
                        className="w-6 h-6 mr-2"
                      />
                      {swap.toMint.symbol}
                    </TableCell>
                    <TableCell>
                      {formatNumberToString(swap.toAmount ?? undefined) ??
                        "N/A"}
                    </TableCell>
                    <TableCell style={{ textWrap: "nowrap" }}>
                      {formatDate(swap.date, "DD-MM-YYYY HH:mm")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}

        <Pagination>
          <PaginationContent>
            <PaginationItem>
              {page >= 1 ? <PaginationPrevious onClick={onPrevious} /> : null}
            </PaginationItem>
            <PaginationItem>
              <PaginationLink>{page + 1}</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext onClick={onNext} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </CardContent>
    </Card>
  );
};

export const SwapsTable = ({ organizationId }: { organizationId: string }) => {
  const [swapPage, setSwapPage] = useState(0);
  const [{ data: swapsData }] = useGetSwapsQuery({
    variables: { page: swapPage, organizationId },
    pause: organizationId === "" || !organizationId,
  });
  const swaps = swapsData?.getSwaps;
  return (
    <SwapsTableBase
      swaps={swaps}
      page={swapPage}
      onPrevious={() => setSwapPage(swapPage - 1)}
      onNext={() => setSwapPage(swapPage + 1)}
    />
  );
};
